<script setup>
import { ref, watch, computed, provide, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import KanbanTab from './components/KanbanTab.vue';
import ListTab from './components/ListTab.vue';
import AgendaTab from './components/AgendaTab.vue';
import KanbanMap from './components/KanbanMap.vue';
import FunnelsManager from './components/FunnelsManager.vue';
import OffersManager from './components/OffersManager.vue';
import OfferPackagesManager from './components/OfferPackagesManager.vue';
import MessageTemplates from './components/MessageTemplates.vue';
import ScheduledMessagesTab from './components/ScheduledMessagesTab.vue';
import KanbanDashboard from './components/KanbanDashboard.vue';
import KanbanStageAutomationsView from './components/KanbanStageAutomationsView.vue';

import KanbanItemDetailView from './components/KanbanItemDetailView.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const currentView = ref('kanban');
const selectedItemId = ref(null);
const forceReloadCounter = ref(0);
const hasLicenseError = ref(false);
const licenseErrorDetails = ref(null);

const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);
const dashboardViewEnabled = computed(
  () => store.getters['kanban/getDashboardViewEnabled']
);
const mapViewEnabled = computed(
  () => store.getters['kanban/getMapViewEnabled']
);
const labelsMap = ref({});
const isPageReady = ref(false);

provide('labelsMap', labelsMap);

const selectFunnelFromQuery = async funnelId => {
  if (!funnelId) return false;

  const funnels = store.getters['funnel/getFunnels'];
  const funnel = funnels.find(f => String(f.id) === String(funnelId));
  if (!funnel) return false;

  if (String(funnel.id) !== String(selectedFunnel.value?.id)) {
    await store.dispatch('funnel/setSelectedFunnel', funnel);
  }

  return true;
};

// Watch para mudanças na rota
watch(
  () => route.query,
  async (newQuery, oldQuery) => {
    // Determinar a view baseada na prioridade: item-detail > funnels > offers > view param > kanban
    if (newQuery.item) {
      const itemId = parseInt(newQuery.item);
      if (itemId !== selectedItemId.value || currentView.value !== 'item-detail') {
        selectedItemId.value = itemId;
        forceReloadCounter.value = forceReloadCounter.value + 1;
        currentView.value = 'item-detail';
      }
    } else if (newQuery.funnel) {
      try {
        await selectFunnelFromQuery(newQuery.funnel);
      } catch (error) {
        if (
          error.response?.status === 403 ||
          error.status === 403 ||
          error.response?.data?.code === 'STACKLAB_TOKEN_NOT_FOUND'
        ) {
          hasLicenseError.value = true;
          licenseErrorDetails.value =
            error.response?.data || error.data || error;
        }
      }
      // Mudar para view de funnels apenas quando há parâmetro 'edit' junto com 'funnel'
      if (newQuery.edit) {
        currentView.value = 'funnels';
      }
      selectedItemId.value = null;
    } else if (newQuery.offer) {
      // Mudar para view de offers quando há parâmetro offer na URL
      currentView.value = 'offers';
      selectedItemId.value = null;
    } else if (newQuery.view === 'offer_packages') {
      currentView.value = 'offer-packages';
      selectedItemId.value = null;
    } else if (newQuery.view === 'scheduled_messages') {
      currentView.value = 'scheduled-messages';
      selectedItemId.value = null;
    } else if (newQuery.view === 'stage_automations') {
      currentView.value = 'stage-automations';
      selectedItemId.value = null;
    } else {
      // Verificar se estamos saindo da edição de um funil (removendo o parâmetro funnel)
      const wasEditingFunnel = oldQuery?.funnel && !newQuery.funnel && currentView.value === 'funnels';
      // Verificar se estamos saindo da edição de uma oferta (removendo o parâmetro offer)
      const wasEditingOffer = oldQuery?.offer && !newQuery.offer && currentView.value === 'offers';

      if (wasEditingFunnel) {
        // Se estávamos editando um funil e removemos o parâmetro, manter na view de funnels
        selectedItemId.value = null;
        return;
      }

      if (wasEditingOffer) {
        // Se estávamos editando uma oferta e removemos o parâmetro, manter na view de offers
        selectedItemId.value = null;
        return;
      }

      // Quando não há item, funnel ou offer específico na URL, usar o parâmetro view ou voltar para kanban
      const newView = newQuery.view || 'kanban';
      if (newView !== currentView.value) {
        currentView.value = newView;
      }
      // Quando não há item na URL, voltar para kanban se estiver na view de detalhe
      if (currentView.value === 'item-detail') {
        currentView.value = newView;
      }
      selectedItemId.value = null;
    }
  },
  { immediate: true, deep: true }
);

// Carregar funis ao montar o componente
onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('funnel/fetch'),
      store.dispatch('agents/get'),
      store.dispatch('inboxes/get'),
    ]);
    await selectFunnelFromQuery(route.query.funnel);
    isPageReady.value = true;
  } catch (error) {
    if (
      error.response?.status === 403 ||
      error.status === 403 ||
      error.response?.data?.code === 'STACKLAB_TOKEN_NOT_FOUND'
    ) {
      hasLicenseError.value = true;
      licenseErrorDetails.value = error.response?.data || error.data || error;
    }
  }
});

const createMode = ref(false);

const switchView = (view, options = {}) => {
  currentView.value = view;
  selectedItemId.value = null;

  // Atualizar URL para manter a view no refresh
  const currentQuery = { ...route.query };

  if (view !== 'stage-automations') {
    delete currentQuery.stage;
  }

  if (view === 'kanban') {
    // Remover parâmetro view quando volta para kanban
    delete currentQuery.view;
    delete currentQuery.offer;
    delete currentQuery.funnel;
    delete currentQuery.item;
    delete currentQuery.tab;
  } else if (view === 'offers') {
    // Para offers, adicionar view=offers mas manter offer se existir
    currentQuery.view = 'offers';
  } else if (view === 'offer-packages') {
    currentQuery.view = 'offer_packages';
    delete currentQuery.offer;
  } else if (view === 'scheduled-messages') {
    currentQuery.view = 'scheduled_messages';
  } else if (view === 'stage-automations') {
    currentQuery.view = 'stage_automations';
    if (options.stageId != null && options.stageId !== '') {
      currentQuery.stage = String(options.stageId);
    }
  } else if (view === 'funnels') {
    // Para funnels, adicionar view=funnels mas manter funnel se existir
    currentQuery.view = 'funnels';
  } else if (view !== 'item-detail') {
    // Para outras views, adicionar o parâmetro view
    currentQuery.view = view;
  }

  // Atualizar URL sem recarregar a página
  router.replace({ query: currentQuery });

  // Se estiver navegando para funnels com mode 'create', ativar modo de criação
  if (view === 'funnels' && options.mode === 'create') {
    createMode.value = true;
  } else {
    createMode.value = false;
  }
};

watch(dashboardViewEnabled, enabled => {
  if (!enabled && currentView.value === 'dashboard') {
    switchView('kanban');
  }
});

watch(mapViewEnabled, enabled => {
  if (!enabled && currentView.value === 'map') {
    switchView('kanban');
  }
});

const handleItemClick = item => {
  selectedItemId.value = item.id;
  forceReloadCounter.value = forceReloadCounter.value + 1;
  currentView.value = 'item-detail';
};
const handleBackFromDetail = () => {
  currentView.value = 'kanban';
  selectedItemId.value = null;
};

const getCurrentComponent = computed(() => {
  switch (currentView.value) {
    case 'dashboard':
      return KanbanDashboard;
    case 'kanban':
      return KanbanTab;
    case 'list':
      return ListTab;
    case 'agenda':
      return AgendaTab;
    case 'map':
      return KanbanMap;
    case 'funnels':
      return FunnelsManager;
    case 'offers':
      return OffersManager;
    case 'offer-packages':
      return OfferPackagesManager;
    case 'templates':
      return MessageTemplates;
    case 'scheduled-messages':
      return ScheduledMessagesTab;
    case 'stage-automations':
      return KanbanStageAutomationsView;

    case 'item-detail':
      return KanbanItemDetailView;
    default:
      return KanbanTab;
  }
});
</script>

<template>
  <div class="flex h-full w-full overflow-hidden">

    <!-- Mensagem de erro de licenciamento -->
    <div
      v-if="hasLicenseError"
      class="w-full h-full bg-slate-50 dark:bg-slate-900 flex items-center justify-center"
    >
      <div class="text-center max-w-md mx-auto p-6">
        <div class="text-red-500 text-6xl mb-4">🔒</div>
        <h2
          class="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2"
        >
          Licença Necessária
        </h2>
        <p class="text-slate-600 dark:text-slate-400">
          Esta instalação não possui uma licença válida para acessar o Kanban.
          Entre em contato com o administrador da plataforma.
        </p>
      </div>
    </div>

    <!-- Conteúdo real quando estiver pronto -->
    <component
      :is="getCurrentComponent"
      v-else
      :key="
        currentView +
        (createMode ? '-create' : '') +
        (currentView === 'stage-automations' && route.query.stage
          ? `-${route.query.stage}`
          : '')
      "
      :currentView="currentView"
      :labels-map="labelsMap"
      :itemId="selectedItemId"
      :force-reload="forceReloadCounter"
      :createMode="createMode"
      @switchView="switchView"
      @itemClick="handleItemClick"
      @back="handleBackFromDetail"
    />
  </div>
</template>

<style scoped>
.flex {
  min-height: 0;
  min-width: 0;
  height: 100%;
  width: 100%;
}
</style>
